import { Box, Typography } from "@mui/material";
import { mesfColors } from "@/setup/ag-grid/mesf-theme";
import type { VisualizerBillet } from "../models/MillVisualizer";
import { tempToGradientId } from "../utils/tempToColor";
import {
	PASS_LINE_Y,
	STAND_SLOT_WIDTH,
	standCenterX,
} from "../utils/viewBoxLayout";
import { VisualizerTooltip } from "./VisualizerTooltip";

interface Props {
	billet: VisualizerBillet;
}

const ENTRY_HEIGHT = 11;
const EXIT_HEIGHT = 3;
// Single-stand capsule (first === last → presenceLength = 0) gets a
// minimum visible width so the operator can still see it.
const MIN_CAPSULE_LENGTH = STAND_SLOT_WIDTH * 0.6;

const BilletTooltipContent = ({ billet }: { billet: VisualizerBillet }) => {
	const span = billet.lastStandPosition - billet.firstStandPosition + 1;
	const stands =
		billet.firstStandPosition === billet.lastStandPosition
			? `Stand ${billet.firstStandPosition}`
			: `Stands ${billet.firstStandPosition} → ${billet.lastStandPosition}`;
	const isSyntheticId = billet.billetId < 0;
	return (
		<Box sx={{ minWidth: 200, fontSize: "0.75rem" }}>
			<Box
				sx={{
					pb: 0.5,
					mb: 0.5,
					borderBottom: `1px solid ${mesfColors.grey[200]}`,
				}}
			>
				<Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>
					Billet
				</Typography>
			</Box>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
				{!isSyntheticId ? (
					<TooltipRow label="Id" value={`#${billet.billetId}`} />
				) : (
					<TooltipRow label="Id" value="not identified" />
				)}
				{billet.heatNumber ? (
					<TooltipRow label="Heat" value={billet.heatNumber} />
				) : null}
				<TooltipRow
					label="Position"
					value={`${stands} (${span} stand${span === 1 ? "" : "s"})`}
				/>
			</Box>
		</Box>
	);
};

const TooltipRow = ({ label, value }: { label: string; value: string }) => (
	<Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5 }}>
		<Typography
			sx={{
				fontSize: "0.7rem",
				color: mesfColors.text.secondary,
				textTransform: "uppercase",
				letterSpacing: "0.03em",
			}}
		>
			{label}
		</Typography>
		<Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
			{value}
		</Typography>
	</Box>
);

export const Billet = ({ billet }: Props) => {
	const firstX = standCenterX(billet.firstStandPosition);
	const lastX = standCenterX(billet.lastStandPosition);

	const ratio = billet.positionRatio;

	const height = ENTRY_HEIGHT + (EXIT_HEIGHT - ENTRY_HEIGHT) * ratio;

	// Length tracks the ACTUAL presence span between first and last HIGH
	// stand. Mass conservation is communicated through height (fat-short
	// at entry → thin-long at exit), not through inflated length. Previous
	// version used Math.max with an invented positionLength, which painted
	// the capsule over stands with IsBilletPresent=false (visible
	// contradiction against the grid and the per-stand status dot).
	const presenceLength = lastX - firstX;
	const length = Math.max(MIN_CAPSULE_LENGTH, presenceLength);

	const cx = (firstX + lastX) / 2;

	// Fixed mid-temp gradient — the SP doesn't expose head temperature.
	const gradientId = tempToGradientId(1000);

	// Two distinct billets render as TWO separate <Billet> components
	// because the frontend tracker assigned them different group ids;
	// this component just draws ONE capsule. The natural rounded ends
	// (rx = height/2) of two adjacent capsules give the visual separation.
	return (
		<VisualizerTooltip title={<BilletTooltipContent billet={billet} />}>
			<g
				className="visualizer-billet"
				transform={`translate(${cx}, ${PASS_LINE_Y})`}
			>
				<rect
					className="visualizer-billet-shape"
					x={-length / 2}
					y={-height / 2}
					width={length}
					height={height}
					rx={height / 2}
					ry={height / 2}
					fill={`url(#${gradientId})`}
					stroke="#7f1d1d"
					strokeWidth={0.6}
				/>
			</g>
		</VisualizerTooltip>
	);
};
